import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { FeatureFlagIdJml } from 'c1gen/features'
import { ExpiresInTimeChip } from 'components/core/time/display/index'
import { useFeatureChecker } from 'components/no-ui/capability'
import { AlertCircle, Info } from 'lucide-react'
import { AppEntitlementUserBindingRef } from 'pbts/c1/api/app/v1/app_refs'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { FC, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { accessProfilesMultiGetApi } from 'redux_toolkit/entitlements'

type AccessProfileExpiryFallbackProps = {
    appId: string
    appEntitlementId: string
    appUserId: string
    directExpiry?: Timestamp
}

/**
 * Component that displays grant expiry time.
 * If the grant has a direct expiry, it shows that.
 * If not, uses the effective expiry from access profiles (computed by backend).
 * If JML feature flag is off, only direct expiry is shown.
 * Uses multiGet pattern for automatic batching and caching.
 */
export const AccessProfileExpiryFallback: FC<AccessProfileExpiryFallbackProps> = ({
    appId,
    appEntitlementId,
    appUserId,
    directExpiry,
}) => {
    const hasJml = useFeatureChecker(FeatureFlagIdJml)

    // Memoize the ref to avoid creating new objects on every render
    // This ensures proper batching and cache deduplication
    const bindingRef = useMemo(
        () => AppEntitlementUserBindingRef.create({ appId, appEntitlementId, appUserId }),
        [appId, appEntitlementId, appUserId],
    )

    // Use multiGet for automatic batching and caching across rows
    // The hook will batch requests and cache results in Redux
    const { data: userProfiles, isLoading, isError } = accessProfilesMultiGetApi.useMultiGet(
        bindingRef,
        { actionable: !!hasJml && !directExpiry },
    )

    // If there's a direct expiry, just show it
    if (directExpiry) {
        return <ExpiresInTimeChip timestamp={directExpiry} />
    }

    // If JML is disabled, show no expiration
    if (!hasJml) {
        return (
            <Typography variant="unconfigured">
                <FormattedMessage
                    id="components_user_list_no_expiration"
                    defaultMessage="No expiration"
                />
            </Typography>
        )
    }

    if (isLoading) {
        return <Skeleton width={80} />
    }

    if (isError) {
        return (
            <Tooltip title="Failed to load expiry">
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <AlertCircle size={16} color="var(--mui-palette-error-main)" />
                </Box>
            </Tooltip>
        )
    }

    // Use the effective expiry computed by the backend (considers unenrollment behaviors)
    const effectiveExpiry = userProfiles?.effectiveExpiryAt

    if (!effectiveExpiry) {
        return (
            <Typography variant="unconfigured">
                <FormattedMessage
                    id="components_user_list_no_expiration"
                    defaultMessage="No expiration"
                />
            </Typography>
        )
    }

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            <ExpiresInTimeChip timestamp={effectiveExpiry} />
            <Tooltip
                title={
                    <FormattedMessage
                        id="access_profile_expiry_warning"
                        defaultMessage="This is an effective expiration date based on access profile expiration"
                    />
                }
            >
                <Info size={16} color="currentColor" style={{ color: 'var(--mui-palette-text-secondary)' }} />
            </Tooltip>
        </Stack>
    )
}
